import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import {
  CoworkCommunityRow,
  CoworkState,
  CoworkTask,
  CoworkTimer,
  OverlayElements,
  OverlayStyle,
} from './types';
import { Celebration } from './useCoworkOverlay';
import {
  accentBarStyle,
  animMs,
  bodyStyle,
  contentIndentFor,
  dividerStyle,
  formatClock,
  formatDuration,
  isSharpShape,
  justifyFor,
  labelStyle,
  mutedStyle,
  panelRadius,
  rowStyleFor,
  surfaceStyle,
  taskMarker,
  taskNumber,
  withAlpha,
} from './styleUtils';

export const CoworkOverlayGlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
    background: transparent !important;
    background-color: transparent !important;
  }
  @keyframes cowork-fade-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes cowork-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }
  @keyframes cowork-celebrate {
    0% { opacity: 0; transform: translateY(10px) scale(0.97); }
    12% { opacity: 1; transform: translateY(0) scale(1); }
    85% { opacity: 1; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(-6px) scale(0.99); }
  }
  @keyframes cowork-slide-up {
    from { transform: translateY(0); }
    to { transform: translateY(-50%); }
  }
`;

interface BlockProps {
  style: OverlayStyle;
  elements: OverlayElements;
}

function enterAnimation(style: OverlayStyle, delayMs = 0): React.CSSProperties {
  const duration = animMs(style, 420);
  if (!duration) return {};
  return { animation: `cowork-fade-in ${duration}ms ease-out ${delayMs}ms both` };
}

/** Alineación de las filas de lista: por defecto van a la izquierda aunque el panel esté centrado. */
function listJustify(style: OverlayStyle): React.CSSProperties['justifyContent'] {
  return style.centerListItems ? justifyFor(style.align) : 'flex-start';
}

/* ------------------------------- Panel ------------------------------- */

const Panel: React.FC<{
  style: OverlayStyle;
  extra?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ style, extra = {}, children }) => {
  const bar = accentBarStyle(style);
  const indent = contentIndentFor(style);

  return (
    <div
      style={surfaceStyle(style, {
        position: 'relative',
        overflow: 'hidden',
        paddingLeft: indent > 0 ? style.padding + indent : undefined,
        ...extra,
        ...enterAnimation(style),
      })}
    >
      {bar && <div style={bar} />}
      {children}
    </div>
  );
};

const BlockTitle: React.FC<{ style: OverlayStyle; children: React.ReactNode }> = ({ style, children }) => (
  <>
    <div style={labelStyle(style)}>{children}</div>
    {style.labelDivider && <div style={dividerStyle(style)} />}
  </>
);

/* ---------------------------- Slide (scroll) -------------------------- */

/**
 * Modo slide: desplaza la lista de abajo hacia arriba en bucle continuo.
 * Solo se activa si el contenido no entra en la altura configurada.
 */
const SlideList: React.FC<{
  style: OverlayStyle;
  enabled: boolean;
  maxHeight: number;
  speed: number;
  children: React.ReactNode;
}> = ({ style, enabled, maxHeight, speed, children }) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const height = maxHeight > 0 ? maxHeight : 320;

  const measure = useCallback(() => {
    const node = innerRef.current;
    if (node) setContentHeight(node.scrollHeight);
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return undefined;
    const node = innerRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, measure]);

  if (!enabled) return <>{children}</>;

  const overflowing = contentHeight > height + 2;
  const pixelsPerSecond = Math.max(4, speed || 25);
  const duration = overflowing ? Math.max(4, contentHeight / pixelsPerSecond) : 0;
  const copyStyle: React.CSSProperties = { paddingBottom: Math.max(8, style.gap) };

  return (
    <div
      style={{
        width: '100%',
        maxHeight: height,
        overflow: 'hidden',
        position: 'relative',
        maskImage: overflowing
          ? 'linear-gradient(to bottom, transparent 0%, #000 9%, #000 91%, transparent 100%)'
          : undefined,
        WebkitMaskImage: overflowing
          ? 'linear-gradient(to bottom, transparent 0%, #000 9%, #000 91%, transparent 100%)'
          : undefined,
      }}
    >
      <div
        style={{
          animation: overflowing && style.animationsEnabled
            ? `cowork-slide-up ${duration}s linear infinite`
            : undefined,
        }}
      >
        <div ref={innerRef} style={copyStyle}>{children}</div>
        {overflowing && <div style={copyStyle} aria-hidden>{children}</div>}
      </div>
    </div>
  );
};

function slideSettings(elements: OverlayElements) {
  return {
    enabled: !!elements.autoScroll,
    maxHeight: Math.max(0, parseInt(elements.maxHeight, 10) || 0),
    speed: Math.max(4, parseInt(elements.scrollSpeed, 10) || 25),
  };
}

/* ------------------------------- Timer ------------------------------- */

/** Línea de configuración: "50/10 (+20) · Ciclo 2 de 4". */
function timerMetaLine(timer: CoworkTimer, elements: OverlayElements): string | null {
  const parts: string[] = [];

  if (elements.showConfig !== false && timer.focusMinutes > 0) {
    let config = `${timer.focusMinutes}/${timer.breakMinutes}`;
    if (timer.longBreakEnabled && timer.longBreakMinutes > 0) {
      config += ` (+${timer.longBreakMinutes})`;
    }
    parts.push(config);
  }

  if (elements.showCycle !== false && timer.totalCycles > 0) {
    parts.push(timer.cycle > 0
      ? `Ciclo ${timer.cycle} de ${timer.totalCycles}`
      : `${timer.totalCycles} ciclos`);
  }

  return parts.length ? parts.join(' · ') : null;
}

export const TimerBlock: React.FC<BlockProps & {
  timer: CoworkTimer;
  remainingMs: number;
  focusTask?: CoworkTask | null;
  bare?: boolean;
}> = ({ style, elements, timer, remainingMs, focusTask, bare }) => {
  const showSeconds = elements.showSeconds !== false;
  const isWarning = style.minuteWarning
    && timer.status === 'running'
    && timer.phase === 'focus'
    && remainingMs > 0
    && remainingMs <= 60000;

  const idle = timer.status === 'idle' || !timer.phase;
  const finished = timer.status === 'finished';

  const progress = timer.phaseDurationMs > 0
    ? Math.max(0, Math.min(1, 1 - remainingMs / timer.phaseDurationMs))
    : 0;

  const clockColor = isWarning ? style.accentColor : style.textColor;
  const pulseMs = animMs(style, 2200);
  const metaLine = timerMetaLine(timer, elements);

  const offsetX = style.timerOffsetX || 0;
  const offsetY = style.timerOffsetY || 0;
  const clockAlign = !style.timerAlign || style.timerAlign === 'inherit' ? style.align : style.timerAlign;

  const clock = (
    <div
      style={{
        fontFamily: style.fontFamily,
        fontSize: style.timerFontSize,
        fontWeight: style.fontWeight,
        letterSpacing: Math.round(style.letterSpacing / 2),
        lineHeight: 1,
        color: clockColor,
        fontVariantNumeric: 'tabular-nums',
        textShadow: style.textShadow ? '0 3px 14px rgba(0, 0, 0, 0.8)' : undefined,
        transition: `color ${animMs(style, 400)}ms ease`,
        animation: isWarning && pulseMs ? `cowork-pulse ${pulseMs}ms ease-in-out infinite` : undefined,
      }}
    >
      {idle && !finished ? (showSeconds ? '00:00' : '0') : formatClock(remainingMs, showSeconds)}
    </div>
  );

  // El reloj se posiciona aparte del resto para poder correrlo sin mover el bloque.
  const clockRow = (
    <div
      style={{
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: justifyFor(clockAlign),
        transform: offsetX || offsetY ? `translate(${offsetX}px, ${offsetY}px)` : undefined,
      }}
    >
      {style.timerBoxed ? (
        <div
          style={{
            display: 'flex',
            padding: `${Math.round(style.padding * 0.3)}px ${Math.round(style.padding * 0.5)}px`,
            border: `${Math.max(1, style.borderWidth)}px solid ${withAlpha(style.accentColor, 0.55)}`,
            borderRadius: isSharpShape(style) ? 0 : Math.round(panelRadius(style) * 0.6),
            background: withAlpha(style.accentColor, 0.08),
          }}
        >
          {clock}
        </div>
      ) : clock}
    </div>
  );

  const content = (
    <>
      {elements.showPhaseName !== false && (
        <div style={labelStyle(style, { opacity: idle ? 0.6 : 1 })}>
          {finished ? 'SESIÓN COMPLETA' : idle ? 'EN PAUSA' : timer.phaseLabel}
          {timer.status === 'paused' && !idle ? ' · PAUSA' : ''}
        </div>
      )}

      {clockRow}

      {metaLine && <div style={mutedStyle(style)}>{metaLine}</div>}

      {elements.showProgressBar !== false && (
          <div
            style={{
              width: '100%',
              height: 4,
              borderRadius: isSharpShape(style) ? 0 : 999,
              background: withAlpha(style.mutedColor, 0.25),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress * 100}%`,
                height: '100%',
                borderRadius: isSharpShape(style) ? 0 : 999,
              background: `linear-gradient(90deg, ${style.accentColor}, ${style.secondaryColor})`,
              transition: `width ${animMs(style, 900)}ms linear`,
            }}
          />
        </div>
      )}

      {elements.showFocusTask && focusTask && (
        <div style={bodyStyle(style, { fontSize: Math.round(style.bodyFontSize * 0.95) })}>
          {focusTask.text}
        </div>
      )}
    </>
  );

  if (bare) return <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, alignItems: 'inherit', width: '100%' }}>{content}</div>;

  return <Panel style={style}>{content}</Panel>;
};

/* ------------------------------- Focus ------------------------------- */

export const FocusBlock: React.FC<BlockProps & {
  focusTask: CoworkTask | null;
  timer: CoworkTimer;
  remainingMs: number;
  bare?: boolean;
}> = ({ style, elements, focusTask, timer, remainingMs, bare }) => {
  const showSeconds = elements.showSeconds !== false;

  const content = (
    <>
      {elements.showTitle !== false && (
        <BlockTitle style={style}>{elements.title || 'EN QUÉ ESTOY TRABAJANDO'}</BlockTitle>
      )}

      <div
        key={focusTask?.id || 'none'}
        style={bodyStyle(style, {
          fontSize: Math.round(style.bodyFontSize * 1.5),
          fontWeight: style.fontWeight,
          ...enterAnimation(style),
        })}
      >
        {focusTask ? focusTask.text : 'Preparando la próxima tarea…'}
      </div>

      {elements.showTimer !== false && timer.phase && (
        <div style={mutedStyle(style, { fontVariantNumeric: 'tabular-nums' })}>
          {formatClock(remainingMs, showSeconds)} restantes
          {elements.showPhaseName ? ` · ${timer.phaseLabel}` : ''}
        </div>
      )}
    </>
  );

  if (bare) return <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, alignItems: 'inherit', width: '100%' }}>{content}</div>;

  return <Panel style={style}>{content}</Panel>;
};

/* ------------------------------- Tasks ------------------------------- */

function TaskRow({
  style,
  task,
  index,
  animate = true,
  compact = false,
  showNumber = false,
}: {
  style: OverlayStyle;
  task: CoworkTask;
  index: number;
  animate?: boolean;
  compact?: boolean;
  showNumber?: boolean;
}) {
  const done = task.status === 'completed';
  const marker = taskMarker(style, task, index);
  // Con el marcador "número" el número ya está en el marcador; con el resto lo agregamos aparte.
  const numberLabel = showNumber && style.markerStyle !== 'number'
    ? `${taskNumber(task, index)}`
    : '';
  const color = done ? style.mutedColor : task.focused ? style.accentColor : style.textColor;
  const fontSize = compact ? Math.round(style.bodyFontSize * 0.88) : style.bodyFontSize;

  return (
    <div
      style={rowStyleFor(style, index, {
        justifyContent: listJustify(style),
        ...(animate ? enterAnimation(style, index * 45) : {}),
      })}
    >
      {numberLabel && (
        <span
          style={{
            color: style.mutedColor,
            fontSize: Math.round(fontSize * 0.9),
            fontFamily: style.bodyFontFamily,
            lineHeight: 1.35,
            flexShrink: 0,
            minWidth: Math.round(fontSize * 1.1),
            textAlign: 'right',
          }}
        >
          {numberLabel}
        </span>
      )}
      <span
        style={{
          color: done ? style.secondaryColor : task.focused ? style.accentColor : style.mutedColor,
          fontSize,
          lineHeight: 1.35,
          flexShrink: 0,
          fontFamily: style.markerStyle === 'bracket' || style.markerStyle === 'number'
            ? style.bodyFontFamily
            : undefined,
          minWidth: style.markerStyle === 'number' ? Math.round(fontSize * 1.4) : undefined,
        }}
      >
        {marker}
      </span>
      <span
        style={bodyStyle(style, {
          fontSize,
          color,
          textDecoration: done ? 'line-through' : 'none',
          opacity: done ? 0.65 : 1,
          fontWeight: task.focused ? style.fontWeight : 400,
          transition: `opacity ${animMs(style, 400)}ms ease`,
        })}
      >
        {task.text}
      </span>
    </div>
  );
}

export const TasksBlock: React.FC<BlockProps & { tasks: CoworkTask[]; bare?: boolean }> = ({
  style,
  elements,
  tasks,
  bare,
}) => {
  const slide = slideSettings(elements);

  const visible = useMemo(() => {
    const showCompleted = elements.showCompleted !== false;
    const filtered = tasks.filter((t) => t.status !== 'archived' && (showCompleted || t.status !== 'completed'));
    // Con el slide activo no se recorta: para eso está el desplazamiento.
    const max = slide.enabled ? filtered.length : Math.max(1, parseInt(elements.maxItems, 10) || 6);

    if (elements.highlightFocus !== false) {
      const focus = filtered.filter((t) => t.focused);
      const rest = filtered.filter((t) => !t.focused);
      return [...focus, ...rest].slice(0, max);
    }
    return filtered.slice(0, max);
  }, [tasks, elements, slide.enabled]);

  const pending = tasks.filter((t) => t.status === 'pending').length;
  const completed = tasks.filter((t) => t.status === 'completed').length;

  const list = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: Math.round(style.gap * 0.55), width: '100%' }}>
      {visible.length === 0 && <div style={mutedStyle(style)}>Sin tareas cargadas.</div>}
      {visible.map((task, index) => (
        <TaskRow key={task.id} style={style} task={task} index={index} animate={!slide.enabled} />
      ))}
    </div>
  );

  const content = (
    <>
      {elements.showTitle !== false && (
        <BlockTitle style={style}>{elements.title || 'HOY'}</BlockTitle>
      )}

      <SlideList style={style} enabled={slide.enabled} maxHeight={slide.maxHeight} speed={slide.speed}>
        {list}
      </SlideList>

      {elements.showCounter !== false && (
        <div style={mutedStyle(style)}>
          {completed} completadas · {pending} pendientes
        </div>
      )}
    </>
  );

  if (bare) return <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, alignItems: 'inherit', width: '100%' }}>{content}</div>;

  return <Panel style={style}>{content}</Panel>;
};

/* ----------------------------- Community ----------------------------- */

function communityTasksFor(
  row: CoworkCommunityRow,
  elements: OverlayElements,
  unlimited: boolean,
): CoworkTask[] {
  const list = Array.isArray(row.tasks) ? row.tasks : [];
  const showCompleted = elements.showCompletedTasks !== false;
  const filtered = list.filter((t) => t.status !== 'archived' && (showCompleted || t.status !== 'completed'));
  // Con el slide activo se muestran todas: el desplazamiento se encarga del alto.
  if (unlimited) return filtered;
  const max = Math.max(1, parseInt(elements.maxTasksPerUser, 10) || 4);
  return filtered.slice(0, max);
}

const CommunityUserRow: React.FC<{
  style: OverlayStyle;
  elements: OverlayElements;
  row: CoworkCommunityRow;
  index: number;
  animate: boolean;
  unlimited: boolean;
}> = ({ style, elements, row, index, animate, unlimited }) => {
  const expanded = (elements.mode || 'expanded') !== 'compact';
  const tasks = expanded ? communityTasksFor(row, elements, unlimited) : [];

  const nameNode = (
    <span
      style={bodyStyle(style, {
        color: style.accentColor,
        fontFamily: style.fontFamily,
        fontWeight: style.fontWeight,
        letterSpacing: Math.round(style.letterSpacing / 2),
        flexShrink: 0,
        maxWidth: expanded ? '100%' : '38%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      })}
    >
      {row.displayName}
    </span>
  );

  if (!expanded) {
    return (
      <div
        style={rowStyleFor(style, index, {
          alignItems: 'baseline',
          ...(animate ? enterAnimation(style, index * 45) : {}),
        })}
      >
        {nameNode}
        <span style={mutedStyle(style, { flexShrink: 0 })}>→</span>
        <span
          style={bodyStyle(style, {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            opacity: row.focusText ? 1 : 0.6,
          })}
        >
          {row.focusText || `${row.pendingCount} pendiente${row.pendingCount === 1 ? '' : 's'}`}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: Math.max(2, Math.round(style.gap * 0.3)),
        width: '100%',
        ...(animate ? enterAnimation(style, index * 45) : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', justifyContent: listJustify(style) }}>
        {nameNode}
        {elements.showUserCounts !== false && (
          <span style={mutedStyle(style, { fontSize: Math.round(style.bodyFontSize * 0.68), flexShrink: 0 })}>
            {row.pendingCount} pend · {row.completedCount} ✓
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: Math.max(2, Math.round(style.gap * 0.28)),
          width: '100%',
          paddingLeft: style.align === 'left' ? Math.round(style.padding * 0.5) : 0,
        }}
      >
        {tasks.length === 0 && (
          <div style={mutedStyle(style, { fontSize: Math.round(style.bodyFontSize * 0.8) })}>
            Sin tareas activas
          </div>
        )}
        {tasks.map((task, taskIndex) => (
          <TaskRow
            key={task.id}
            style={style}
            task={task}
            index={taskIndex}
            animate={false}
            compact
            showNumber={elements.showTaskNumbers !== false}
          />
        ))}
      </div>
    </div>
  );
};

export const CommunityBlock: React.FC<BlockProps & {
  community: CoworkCommunityRow[];
  celebrations: Celebration[];
  bare?: boolean;
}> = ({ style, elements, community, celebrations, bare }) => {
  const slide = slideSettings(elements);
  const expanded = (elements.mode || 'expanded') !== 'compact';

  const rows = useMemo(() => {
    let list = [...community];
    if (elements.onlyFocused) list = list.filter((row) => !!row.focusText);

    if (elements.order === 'alphabetical') {
      list.sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    } else if (elements.order === 'completed') {
      list.sort((a, b) => b.completedCount - a.completedCount);
    }

    if (slide.enabled) return list;
    const max = Math.max(1, parseInt(elements.maxUsers, 10) || 8);
    return list.slice(0, max);
  }, [community, elements, slide.enabled]);

  const totalCompleted = community.reduce((sum, row) => sum + row.completedCount, 0);
  const celebrateSeconds = Math.max(0, parseInt(elements.showCompletedSeconds, 10) || 0);
  const activeCelebration = style.celebrateCompleted && celebrateSeconds > 0
    ? celebrations.filter((c) => Date.now() - c.at < celebrateSeconds * 1000).slice(-1)[0]
    : undefined;

  const list = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? Math.max(6, Math.round(style.gap * 0.9)) : Math.round(style.gap * 0.55),
        width: '100%',
      }}
    >
      {rows.length === 0 && (
        <div style={mutedStyle(style)}>Sumate con !tarea &lt;lo que vas a hacer&gt;</div>
      )}
      {rows.map((row, index) => (
        <CommunityUserRow
          key={row.twitchId}
          style={style}
          elements={elements}
          row={row}
          index={index}
          animate={!slide.enabled}
          unlimited={slide.enabled}
        />
      ))}
    </div>
  );

  const content = (
    <>
      {elements.showTitle !== false && (
        <BlockTitle style={style}>{elements.title || 'COWORK CREW'}</BlockTitle>
      )}

      <SlideList style={style} enabled={slide.enabled} maxHeight={slide.maxHeight} speed={slide.speed}>
        {list}
      </SlideList>

      {elements.showCounter !== false && (
        <div style={mutedStyle(style)}>
          {community.length} en cowork · {totalCompleted} tareas completadas
        </div>
      )}

      {activeCelebration && (
        <div
          key={activeCelebration.id}
          style={bodyStyle(style, {
            width: '100%',
            padding: `${Math.round(style.padding * 0.4)}px ${Math.round(style.padding * 0.6)}px`,
            borderRadius: isSharpShape(style) ? 0 : Math.round(panelRadius(style) * 0.7),
            background: withAlpha(style.accentColor, 0.16),
            border: `1px solid ${withAlpha(style.accentColor, 0.35)}`,
            fontSize: Math.round(style.bodyFontSize * 0.9),
            animation: animMs(style, celebrateSeconds * 1000)
              ? `cowork-celebrate ${celebrateSeconds * 1000}ms ease-out both`
              : undefined,
          })}
        >
          ✓ {activeCelebration.displayName} completó «{activeCelebration.text}»
        </div>
      )}
    </>
  );

  if (bare) return <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, alignItems: 'inherit', width: '100%' }}>{content}</div>;

  return <Panel style={style}>{content}</Panel>;
};

/* ------------------------------- Full -------------------------------- */

export const FullOverlay: React.FC<BlockProps & {
  state: CoworkState;
  remainingMs: number;
  celebrations: Celebration[];
}> = ({ style, elements, state, remainingMs, celebrations }) => {
  const layout = elements.layout || 'stacked';

  const panel = (children: React.ReactNode, extra: React.CSSProperties = {}) => (
    <Panel style={style} extra={{ minWidth: 0, ...extra }}>{children}</Panel>
  );

  const timerNode = elements.showTimer !== false && (
    <TimerBlock
      bare
      style={style}
      elements={{
        showPhaseName: true,
        showSeconds: elements.showSeconds !== false,
        showCycle: elements.showCycle !== false,
        showConfig: elements.showConfig !== false,
        showProgressBar: true,
      }}
      timer={state.timer}
      remainingMs={remainingMs}
    />
  );

  const focusNode = elements.showFocus !== false && (
    <FocusBlock
      bare
      style={style}
      elements={{ title: 'FOCUS DE EUGE', showTitle: true, showTimer: false }}
      focusTask={state.focusTask}
      timer={state.timer}
      remainingMs={remainingMs}
    />
  );

  const tasksNode = elements.showTasks !== false && (
    <TasksBlock
      bare
      style={style}
      elements={{
        title: 'MIS TAREAS',
        showTitle: true,
        maxItems: elements.maxItems || 5,
        showCompleted: true,
        showCounter: false,
        highlightFocus: true,
        autoScroll: !!elements.autoScroll,
        scrollSpeed: elements.scrollSpeed,
        maxHeight: elements.maxHeight,
      }}
      tasks={state.streamerTasks}
    />
  );

  const communityNode = elements.showCommunity !== false && (
    <CommunityBlock
      bare
      style={style}
      elements={{
        title: 'ACTIVIDAD DE LA COMUNIDAD',
        showTitle: true,
        mode: elements.communityMode || 'expanded',
        maxUsers: elements.maxUsers || 6,
        maxTasksPerUser: elements.maxTasksPerUser || 3,
        showCompletedTasks: true,
        showCounter: true,
        showCompletedSeconds: 10,
        autoScroll: !!elements.communityAutoScroll,
        scrollSpeed: elements.communityScrollSpeed,
        maxHeight: elements.communityMaxHeight,
      }}
      community={state.community}
      celebrations={celebrations}
    />
  );

  if (layout === 'split') {
    return (
      <div style={{ display: 'flex', gap: style.gap, width: '100%', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, flex: '0 0 42%', minWidth: 0 }}>
          {timerNode && panel(timerNode)}
          {focusNode && panel(focusNode)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, flex: 1, minWidth: 0 }}>
          {tasksNode && panel(tasksNode)}
          {communityNode && panel(communityNode)}
        </div>
      </div>
    );
  }

  if (layout === 'sidebar') {
    return (
      <div style={{ display: 'flex', gap: style.gap, width: '100%', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, flex: 1, minWidth: 0 }}>
          {timerNode && panel(timerNode, { alignItems: 'center', textAlign: 'center' })}
          {focusNode && panel(focusNode)}
          {tasksNode && panel(tasksNode)}
        </div>
        {communityNode && (
          <div style={{ flex: '0 0 34%', minWidth: 0, display: 'flex' }}>
            {panel(communityNode, { flex: 1 })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: style.gap, width: '100%' }}>
      {timerNode && panel(timerNode)}
      {focusNode && panel(focusNode)}
      {tasksNode && panel(tasksNode)}
      {communityNode && panel(communityNode)}
    </div>
  );
};

/* ------------------------------ Stats -------------------------------- */

export function statsSummary(state: CoworkState): string {
  return `Focus de hoy: ${formatDuration(state.stats.focusMs)} · Ciclos: ${state.stats.cycles}`;
}
